import React, { useState } from 'react';
import { PlusIcon, CheckIcon } from 'lucide-react';
type Tab = 'Signature Cocktails' | 'Starters & Sides' | 'Entrées';
interface MenuItem {
  name: string;
  description: string;
  price: string;
  image: string;
}
const menuData: Record<Tab, MenuItem[]> = {
  'Signature Cocktails': [{
    name: 'Palm Wine Spritz',
    description: 'Traditional palm wine elevated with prosecco, fresh mint, and a hint of elderflower.',
    price: '$20',
    image: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=800&q=80'
  }, {
    name: 'Hibiscus Margarita',
    description: 'Zobo (hibiscus) infusion, premium tequila, fresh lime, with a chili-salt rim.',
    price: '$22',
    image: 'https://images.unsplash.com/photo-1536935338788-846bb9981813?w=800&q=80'
  }],
  'Starters & Sides': [{
    name: 'Suya Skewers',
    description: 'Spicy, peanut-crusted grilled beef skewers served with fresh red onions and tomatoes.',
    price: '$22',
    image: 'https://images.unsplash.com/photo-1528735602780-2552fd46c7af?w=800&q=80'
  }, {
    name: 'Kelewele & Puff Puff',
    description: 'Spiced fried plantains paired with our signature sweet, golden dough balls.',
    price: '$20',
    image: 'https://images.unsplash.com/photo-1604329760661-e71dc83f8f26?w=800&q=80'
  }],
  Entrées: [{
    name: 'Jollof Rice & Grilled Chicken',
    description: 'Classic smoky West African party rice served with perfectly charred, spiced chicken and fried plantains.',
    price: '$28',
    image: 'https://images.unsplash.com/photo-1604329760661-e71dc83f8f26?w=800&q=80'
  }, {
    name: 'Egusi Soup with Pounded Yam',
    description: 'Rich melon seed soup cooked with spinach, assorted meats, and palm oil, served with smooth pounded yam.',
    price: '$30',
    image: 'https://images.unsplash.com/photo-1515443961218-a51367888e4b?w=800&q=80'
  }]
};
interface CulinaryGridProps {
  onAddToCart?: (item: {
    id: string;
    name: string;
    price: number;
    image: string;
  }) => void;
}
export function CulinaryGrid({
  onAddToCart
}: CulinaryGridProps) {
  const [activeTab, setActiveTab] = useState<Tab>('Signature Cocktails');
  const [addedItems, setAddedItems] = useState<Set<string>>(new Set());
  const tabs: Tab[] = ['Signature Cocktails', 'Starters & Sides', 'Entrées'];
  const handleAddToCart = (item: MenuItem) => {
    const priceNum = parseFloat(item.price.replace('$', ''));
    const id = item.name.toLowerCase().replace(/\s+/g, '-');
    if (onAddToCart) {
      onAddToCart({
        id,
        name: item.name,
        price: priceNum,
        image: item.image
      });
    }
    setAddedItems(prev => new Set(prev).add(id));
    setTimeout(() => {
      setAddedItems(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 1500);
  };
  return <section id="menu" className="bg-matte py-28 md:py-36 px-6 fade-section" data-id="element-35">
      <div className="max-w-7xl mx-auto" data-id="element-36">
        <div className="text-center mb-16" data-id="element-37">
          <h2 className="font-sans text-sm tracking-mega-wide text-gold mb-4 uppercase" data-id="element-38">
            A Taste Of
          </h2>
          <h3 className="font-serif text-[38px] md:text-[50px] text-white" data-id="element-39">
            TEMPOO
          </h3>
        </div>

        <div className="flex flex-wrap justify-center gap-8 md:gap-16 mb-16 border-b border-white/10 pb-4" data-id="element-40">
          {tabs.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`relative font-sans text-base uppercase tracking-widest pb-4 transition-colors duration-300 ${activeTab === tab ? 'text-white' : 'text-white/40 hover:text-white/70'}`} data-id="element-41">
              {tab}
              {activeTab === tab && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gold" data-id="element-42" />}
            </button>)}
        </div>

        <div className="flex flex-col gap-12 md:gap-24" data-id="element-43">
          {menuData[activeTab].map((item, index) => {
          const itemId = item.name.toLowerCase().replace(/\s+/g, '-');
          const justAdded = addedItems.has(itemId);
          return <div key={index} className={`grid grid-cols-1 md:grid-cols-2 gap-0 items-stretch ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`} data-id="element-44">
                <div className={`relative overflow-hidden group h-[400px] md:h-[500px] ${index % 2 !== 0 ? 'md:order-2' : ''}`} data-id="element-45">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-46" />
                </div>

                <div className={`bg-charcoal p-12 md:p-16 flex flex-col justify-center ${index % 2 !== 0 ? 'md:order-1' : ''}`} data-id="element-47">
                  <h4 className="font-serif text-[26px] md:text-[32px] text-white mb-4" data-id="element-48">
                    {item.name}
                  </h4>
                  <p className="font-sans text-lg text-white/60 leading-relaxed mb-8 max-w-md" data-id="element-49">
                    {item.description}
                  </p>
                  <div className="flex items-center justify-between" data-id="element-50">
                    <span className="font-sans text-gold text-xl tracking-wide" data-id="element-51">
                      {item.price}
                    </span>
                    {onAddToCart && <button onClick={() => handleAddToCart(item)} className={`flex items-center gap-2 px-6 py-3 font-sans uppercase tracking-widest text-sm font-medium transition-all duration-300 ${justAdded ? 'bg-gold text-black' : 'border border-gold text-gold hover:bg-gold/10'}`} data-id="element-52">
                        {justAdded ? <>
                            <CheckIcon className="w-4 h-4" data-id="element-53" />
                            Added
                          </> : <>
                            <PlusIcon className="w-4 h-4" data-id="element-54" />
                            Add to Order
                          </>}
                      </button>}
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}